import React, { useState } from "react";
import { Button } from "primereact/button";
import { MmddyyyyCalendar } from "../../../components/date-input/MmddyyyyCalendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import * as client from "../../../client";
import { STATE_ABBREV_TO_STATE_NAME, StateAbbrev } from "../../../datasets";
import {
  DEFAULT_REINSURER_INFO,
  REINSURER_FIELDS,
  VALUATION_FIELDS,
} from "../general-input/policyInfoOptions";
import {
  perilSentenceFromEnabledPerils,
  readableNameFromPeril,
} from "../general-input/util";
import {
  applyDeductibleAdd,
  applyDeductibleDelete,
  applyDeductibleEdit,
  applyLimitAdd,
  applyLimitDelete,
  applyLimitEdit,
  applySublimitAdd,
  applySublimitDelete,
  applySublimitEdit,
} from "../policy/policyEdits";
import {
  BrokerCommissionInput,
  FormSelectionDropdown,
} from "../policy-terms/PolicyTermControls";
import { isPerilLocked, togglePeril } from "../policy-terms/perilRules";
import { TermWordingBanner } from "../policy/termWording";
import { getSelectablePolicyFormNames } from "../utils";
import { ExcerptSection } from "./ExcerptSection";
import {
  EditableRow,
  FieldRow,
  ReadOnlyRow,
  RowBadge,
  Section,
} from "./FieldRow";
import {
  PERIL_ORDER,
  SUBLIMIT_BLOCKS,
  perilExcerptGroups,
  sublimitClassificationForBlock,
  sublimitGroups,
} from "./excerptGroups";
import { SubmissionDraft, SubmissionDraftState } from "./submissionDraft";
import { formatCurrency } from "./util";
import { v4 as uuid } from "uuid";
import styles from "./OverviewPanes.module.css";

type TextCompanyKey =
  | "named_insured"
  | "dba"
  | "company_address"
  | "company_city"
  | "company_state"
  | "company_zip"
  | "effective_date"
  | "expiration_date"
  | "files_received_date";

const fieldValue = (
  fields: client.ResolvedField[],
  key: string
): client.ResolvedField["value"] =>
  fields.find((field) => field.key === key)?.value ?? null;

const percent = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : `${value}%`;

const STATE_OPTIONS = (
  Object.keys(STATE_ABBREV_TO_STATE_NAME) as StateAbbrev[]
).map((code) => ({
  label: `${code} · ${STATE_ABBREV_TO_STATE_NAME[code]}`,
  value: code,
}));

const stateLabel = (code: string): string =>
  code in STATE_ABBREV_TO_STATE_NAME
    ? `${code} · ${STATE_ABBREV_TO_STATE_NAME[code as StateAbbrev]}`
    : code;

interface SubmissionPaneProps {
  report: client.ReportResponse;
  layer: client.Layer | undefined;
  policyCount: number;
  /** Resolved fields, for the computed schedule facts (TIV, coverages). */
  fields: client.ResolvedField[];
  submission: SubmissionDraftState & { draft: SubmissionDraft };
}

/**
 * The report-owned half of the overview. Every edit lands in the submission
 * draft and saves through the standard report update, exactly as the General,
 * Layers and Policy Fields tabs save, so it is shared by every policy.
 */
export const SubmissionPane: React.FC<SubmissionPaneProps> = ({
  report,
  layer,
  policyCount,
  fields,
  submission,
}) => {
  const { draft, update, updateExcerpts } = submission;
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const editing = (key: string) => editingKeys.has(key);
  const toggleEdit = (key: string) =>
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setCompany = (key: TextCompanyKey, value: string) =>
    update((d) => ({ ...d, companyInfo: { ...d.companyInfo, [key]: value } }));
  const setPolicy = (edit: (policyInfo: client.PolicyInfo) => void) =>
    update((d) => {
      edit(d.policyInfo);
      return d;
    });
  const setLayerTerms = (
    edit: (terms: SubmissionDraft["layerTerms"]) => void
  ) =>
    update((d) => {
      edit(d.layerTerms);
      return d;
    });

  const company = draft.companyInfo;
  const policyInfo = draft.policyInfo;
  const { layerTerms } = draft;

  // A layer override wins over the report term, so the row edits the override
  // while one is in force and offers to drop it.
  const effectivePerils = layerTerms.perils ?? policyInfo.included_perils;
  const effectiveForm =
    layerTerms.formSelection ?? policyInfo.form_selection ?? null;
  const effectiveCommission =
    layerTerms.commission ?? policyInfo.broker_commission_percentage ?? null;

  const layerBadge = <RowBadge tone="layer">Layer override</RowBadge>;
  const unlink = (onClick: () => void) => (
    <Button
      icon="pi pi-link"
      rounded
      text
      size="small"
      type="button"
      tooltip="Drop the layer override and follow the submission"
      tooltipOptions={{ showDelay: 400 }}
      onClick={onClick}
    />
  );

  const textEditor = (key: TextCompanyKey) => (
    <InputText
      autoFocus
      className={styles.input}
      value={company[key] ?? ""}
      onChange={(e) => setCompany(key, e.target.value)}
    />
  );

  const dateEditor = (key: TextCompanyKey) => (
    <MmddyyyyCalendar
      value={company[key] ?? ""}
      onChange={(value) => setCompany(key, value)}
    />
  );

  const dateRow = (key: TextCompanyKey, label: string) => (
    <EditableRow
      label={label}
      display={company[key] ?? ""}
      editing={editing(key)}
      onToggle={() => toggleEdit(key)}
      editor={dateEditor(key)}
    />
  );

  // The broker team the submission came from. A report without one prints an
  // unknown recipient on the quote; the team is set from the report header.
  const broker = fieldValue(fields, "broker:team");

  const cityStateZip = [
    company.company_city,
    [company.company_state, company.company_zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const mailingDisplay = [company.company_address, cityStateZip]
    .filter(Boolean)
    .join("\n");

  const reinsurers = policyInfo.reinsurer_info ?? DEFAULT_REINSURER_INFO;
  const shareTotal = REINSURER_FIELDS.reduce(
    (sum, field) => sum + (reinsurers[field.key] ?? 0),
    0
  );

  const selectedForm = policyInfo.selected_policy_form_name ?? null;
  const formOptions = getSelectablePolicyFormNames(
    report.report_json.attachment_info,
    draft.deductibleInfo,
    draft.limitInfo
  );

  const limitGroups = perilExcerptGroups(
    selectedForm ? (draft.limitInfo?.extracted_limits[selectedForm] ?? {}) : {},
    draft.limitInfo?.manual_limits ?? {},
    effectivePerils
  );
  const deductibleGroups = perilExcerptGroups(
    selectedForm
      ? (draft.deductibleInfo?.extracted_deductibles[selectedForm] ?? {})
      : {},
    draft.deductibleInfo?.manual_deductibles ?? {},
    effectivePerils
  );
  const sublimitBlocks = sublimitGroups(draft.sublimitInfo, selectedForm);
  const perilTargets = PERIL_ORDER.map((peril) => ({
    label: readableNameFromPeril(peril),
    value: peril,
  }));
  const policyCode = selectedForm ?? undefined;
  const excerptHint = "Shared by every policy";
  const termWording = report.term_wording;

  return (
    <div className={styles.pane}>
      <header className={styles.paneHeader}>
        <div className={styles.paneTitleRow}>
          <h2 className={styles.paneTitle}>Submission</h2>
        </div>
        <p className={styles.paneSub}>
          Shared by{" "}
          {policyCount === 1 ? "this policy" : `all ${policyCount} policies`}.
          Edits here update the report.
        </p>
      </header>

      <Section title="Insured">
        <EditableRow
          label="Named insured"
          display={company.named_insured ?? ""}
          editing={editing("named_insured")}
          onToggle={() => toggleEdit("named_insured")}
          editor={textEditor("named_insured")}
        />
        <EditableRow
          label="DBA"
          display={company.dba ?? ""}
          editing={editing("dba")}
          onToggle={() => toggleEdit("dba")}
          editor={textEditor("dba")}
        />
        <EditableRow
          label="Mailing address"
          display={mailingDisplay}
          editing={editing("mailing")}
          onToggle={() => toggleEdit("mailing")}
          editor={
            <div className={styles.inputGrid}>
              <InputText
                autoFocus
                placeholder="Street"
                value={company.company_address ?? ""}
                onChange={(e) => setCompany("company_address", e.target.value)}
              />
              <div className={styles.inputTrio}>
                <InputText
                  placeholder="City"
                  value={company.company_city ?? ""}
                  onChange={(e) => setCompany("company_city", e.target.value)}
                />
                <InputText
                  placeholder="ST"
                  value={company.company_state ?? ""}
                  onChange={(e) => setCompany("company_state", e.target.value)}
                />
                <InputText
                  placeholder="ZIP"
                  value={company.company_zip ?? ""}
                  onChange={(e) => setCompany("company_zip", e.target.value)}
                />
              </div>
            </div>
          }
        />
        <ReadOnlyRow
          label="Broker team"
          value={broker ? String(broker) : null}
          note={
            broker ? "From the submission inbox" : "Set on the report header"
          }
        />
        <EditableRow
          label="Broker on the deal"
          hint="Printed under the team on the quote"
          display={policyInfo.deal_broker_name ?? ""}
          editing={editing("deal_broker_name")}
          onToggle={() => toggleEdit("deal_broker_name")}
          editor={
            <InputText
              autoFocus
              className={styles.input}
              value={policyInfo.deal_broker_name ?? ""}
              onChange={(e) =>
                setPolicy((info) => {
                  info.deal_broker_name = e.target.value || null;
                })
              }
            />
          }
        />
      </Section>

      <Section title="Policy period">
        {dateRow("effective_date", "Effective date")}
        {dateRow("expiration_date", "Expiration date")}
        {dateRow("files_received_date", "Schedule received")}
      </Section>

      <Section title="Coverage">
        <EditableRow
          label="Policy type"
          display={effectiveForm ?? ""}
          editing={editing("form_selection")}
          onToggle={() => toggleEdit("form_selection")}
          badge={layerTerms.formSelection !== null ? layerBadge : undefined}
          extra={
            layerTerms.formSelection !== null
              ? unlink(() =>
                  setLayerTerms((terms) => {
                    terms.formSelection = null;
                  })
                )
              : undefined
          }
          editor={
            <FormSelectionDropdown
              hideLabel
              value={effectiveForm}
              onChange={(value) =>
                layerTerms.formSelection !== null
                  ? setLayerTerms((terms) => {
                      terms.formSelection = value;
                    })
                  : setPolicy((info) => {
                      info.form_selection = value;
                    })
              }
            />
          }
        />
        <FieldRow
          label="Perils"
          actions={
            layerTerms.perils !== null
              ? unlink(() =>
                  setLayerTerms((terms) => {
                    terms.perils = null;
                  })
                )
              : undefined
          }
        >
          <div className={styles.chips}>
            {PERIL_ORDER.map((peril) => {
              const on = effectivePerils.includes(peril);
              const locked = isPerilLocked(effectivePerils, peril);
              return (
                <button
                  key={peril}
                  type="button"
                  className={`${styles.chip} ${on ? styles.chipOn : ""} ${
                    locked ? styles.chipLocked : ""
                  }`}
                  disabled={locked}
                  title={locked ? "Included with All Other Perils" : undefined}
                  onClick={() => {
                    const next = togglePeril(effectivePerils, peril, !on);
                    if (layerTerms.perils !== null) {
                      setLayerTerms((terms) => {
                        terms.perils = next;
                      });
                    } else {
                      setPolicy((info) => {
                        info.included_perils = next;
                      });
                    }
                  }}
                >
                  {readableNameFromPeril(peril)}
                </button>
              );
            })}
          </div>
          {layerTerms.perils !== null && layerBadge}
        </FieldRow>
        <EditableRow
          label="Perils insured"
          hint="Sentence the quote prints"
          display={perilSentenceFromEnabledPerils(
            effectivePerils,
            policyInfo.peril_sentence_override
          )}
          editing={editing("peril_sentence")}
          onToggle={() => toggleEdit("peril_sentence")}
          badge={
            policyInfo.peril_sentence_override ? (
              <RowBadge tone="custom">Custom</RowBadge>
            ) : undefined
          }
          extra={
            policyInfo.peril_sentence_override ? (
              <Button
                icon="pi pi-replay"
                rounded
                text
                size="small"
                type="button"
                tooltip="Use the generated sentence"
                tooltipOptions={{ showDelay: 400 }}
                onClick={() =>
                  setPolicy((info) => {
                    info.peril_sentence_override = null;
                  })
                }
              />
            ) : undefined
          }
          editor={
            <InputTextarea
              autoFocus
              autoResize
              rows={1}
              className={styles.input}
              value={perilSentenceFromEnabledPerils(
                effectivePerils,
                policyInfo.peril_sentence_override
              )}
              onChange={(e) =>
                setPolicy((info) => {
                  info.peril_sentence_override = e.target.value;
                })
              }
            />
          }
        />
        <EditableRow
          label="Broker commission"
          display={percent(effectiveCommission)}
          editing={editing("commission")}
          onToggle={() => toggleEdit("commission")}
          badge={layerTerms.commission !== null ? layerBadge : undefined}
          extra={
            layerTerms.commission !== null
              ? unlink(() =>
                  setLayerTerms((terms) => {
                    terms.commission = null;
                  })
                )
              : undefined
          }
          editor={
            <BrokerCommissionInput
              hideLabel
              value={effectiveCommission}
              onChange={(value) =>
                layerTerms.commission !== null
                  ? setLayerTerms((terms) => {
                      terms.commission = value;
                    })
                  : setPolicy((info) => {
                      info.broker_commission_percentage = value;
                    })
              }
            />
          }
        />
        <EditableRow
          label="Policy form document"
          hint="Source of the limits, sublimits and deductibles below"
          display={selectedForm ?? ""}
          editing={editing("selected_form")}
          onToggle={() => toggleEdit("selected_form")}
          editor={
            <Dropdown
              options={formOptions.map((name) => ({
                label: name,
                value: name,
              }))}
              value={selectedForm}
              placeholder="Select a form…"
              onChange={(e) =>
                setPolicy((info) => {
                  info.selected_policy_form_name = e.value ?? null;
                })
              }
            />
          }
        />
      </Section>

      <Section title="Insuring companies" hint="Participation split">
        <div className={styles.shareBar}>
          {REINSURER_FIELDS.map((field) => (
            <div
              key={field.key}
              className={styles.shareSeg}
              style={{
                width: `${Math.max(0, Math.min(100, reinsurers[field.key] ?? 0))}%`,
              }}
            />
          ))}
        </div>
        {REINSURER_FIELDS.map((field) => (
          <EditableRow
            key={field.key}
            label={field.label}
            display={percent(reinsurers[field.key])}
            editing={editing(field.key)}
            onToggle={() => toggleEdit(field.key)}
            editor={
              <InputNumber
                autoFocus
                value={reinsurers[field.key]}
                suffix="%"
                minFractionDigits={0}
                maxFractionDigits={2}
                min={0}
                max={100}
                onChange={(e) =>
                  setPolicy((info) => {
                    info.reinsurer_info = {
                      ...DEFAULT_REINSURER_INFO,
                      ...info.reinsurer_info,
                      [field.key]: e.value ?? 0,
                    };
                  })
                }
              />
            }
          />
        ))}
        {Math.abs(shareTotal - 100) > 0.01 && (
          <div className={styles.shareWarning}>
            <i className="pi pi-exclamation-triangle" /> Shares total{" "}
            {shareTotal}%, not 100%
          </div>
        )}
      </Section>

      <Section title="Schedule">
        <ReadOnlyRow
          label="Total insured value"
          value={(() => {
            const tiv = fieldValue(fields, "schedule:tiv");
            return typeof tiv === "number" ? formatCurrency(tiv) : null;
          })()}
          note="From the schedule"
        />
        {(() => {
          // The resolved field already applies a saved override; the draft's
          // unsaved override wins while editing.
          const derived = String(
            fieldValue(fields, "schedule:home_state") ?? ""
          );
          const override = policyInfo.home_state_override ?? null;
          const effective = override ?? derived;
          return (
            <EditableRow
              label="Home state"
              hint="Picks the surplus lines notices"
              display={effective ? stateLabel(effective) : ""}
              editing={editing("home_state")}
              onToggle={() => toggleEdit("home_state")}
              badge={
                override ? <RowBadge tone="custom">Custom</RowBadge> : undefined
              }
              extra={
                override ? (
                  <Button
                    icon="pi pi-replay"
                    rounded
                    text
                    size="small"
                    type="button"
                    tooltip="Use the state holding the most TIV"
                    tooltipOptions={{ showDelay: 400 }}
                    onClick={() =>
                      setPolicy((info) => {
                        info.home_state_override = null;
                      })
                    }
                  />
                ) : undefined
              }
              editor={
                <Dropdown
                  autoFocus
                  filter
                  className={styles.input}
                  options={STATE_OPTIONS}
                  value={effective || null}
                  placeholder="Select a state…"
                  onChange={(e) =>
                    setPolicy((info) => {
                      info.home_state_override = e.value || null;
                    })
                  }
                />
              }
            />
          );
        })()}
        <ReadOnlyRow
          label="Coverages"
          value={String(fieldValue(fields, "schedule:coverages") ?? "")}
          note="From the schedule"
        />
        {VALUATION_FIELDS.map((valuation) => {
          const value = policyInfo.valuation_info?.[valuation.key] ?? null;
          return (
            <EditableRow
              key={valuation.key}
              label={valuation.label}
              display={value ?? ""}
              editing={editing(valuation.key)}
              onToggle={() => toggleEdit(valuation.key)}
              editor={
                <Dropdown
                  editable
                  options={valuation.options.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  value={value}
                  placeholder="Select or type a valuation basis…"
                  onChange={(e) =>
                    setPolicy((info) => {
                      info.valuation_info = {
                        physical_valuation_type_1: null,
                        physical_valuation_type_2: null,
                        bi_valuation_type: null,
                        equipment_valuation_type: null,
                        ...info.valuation_info,
                        [valuation.key]: e.value || null,
                      };
                    })
                  }
                />
              }
            />
          );
        })}
      </Section>

      <TermWordingBanner termWording={termWording} />

      <ExcerptSection
        title="Limits"
        hint={excerptHint}
        groups={limitGroups}
        addTargets={perilTargets}
        addLabel="Add limit"
        termWording={termWording}
        emptyText={
          selectedForm
            ? "No limits extracted from this form."
            : "Select a policy form document to load its limits."
        }
        onEdit={(group, row, name, value) =>
          updateExcerpts((d) => ({
            ...d,
            limitInfo: applyLimitEdit(d.limitInfo, {
              perilType: row.peril,
              policyCode,
              oldName: row.name,
              newName: name,
              newValue: value,
            }),
          }))
        }
        onRemove={(group, row) =>
          updateExcerpts((d) => ({
            ...d,
            limitInfo: applyLimitDelete(d.limitInfo, {
              perilType: row.peril,
              policyCode,
              name: row.name,
            }),
          }))
        }
        onAdd={(peril, name, value) =>
          updateExcerpts((d) => ({
            ...d,
            limitInfo: applyLimitAdd(d.limitInfo, {
              perilType: peril as client.peril_type,
              policyCode,
              name,
              value,
            }),
          }))
        }
      />

      <ExcerptSection
        title="Sublimits"
        hint={excerptHint}
        groups={sublimitBlocks}
        addTargets={SUBLIMIT_BLOCKS.map((block) => ({
          label: block.title,
          value: block.key,
        }))}
        addLabel="Add sublimit"
        termWording={termWording}
        emptyText={
          selectedForm
            ? "No sublimits extracted from this form."
            : "Select a policy form document to load its sublimits."
        }
        onEdit={(group, row, name, value) =>
          updateExcerpts((d) => ({
            ...d,
            sublimitInfo: d.sublimitInfo
              ? applySublimitEdit(d.sublimitInfo, {
                  manual: row.manual,
                  policyCode,
                  id: row.sublimit.id ?? null,
                  name: row.name,
                  classification: row.sublimit.classification ?? null,
                  documentExcerpt: row.sublimit.document_excerpt ?? null,
                  manualRowId: uuid(),
                  newName: name,
                  newValue: value,
                })
              : d.sublimitInfo,
          }))
        }
        onRemove={(group, row) =>
          updateExcerpts((d) => ({
            ...d,
            sublimitInfo: d.sublimitInfo
              ? applySublimitDelete(d.sublimitInfo, {
                  manual: row.manual,
                  policyCode,
                  id: row.sublimit.id ?? null,
                  name: row.name,
                  classification: row.sublimit.classification ?? null,
                })
              : d.sublimitInfo,
          }))
        }
        onAdd={(blockKey, name, value) => {
          const classification = sublimitClassificationForBlock(blockKey);
          updateExcerpts((d) => ({
            ...d,
            sublimitInfo: applySublimitAdd(
              d.sublimitInfo ?? {
                extracted_sublimits: {},
                manual_sublimits: [],
              },
              { id: uuid(), name, value, classification }
            ),
          }));
        }}
      />

      <ExcerptSection
        title="Deductibles"
        hint={excerptHint}
        groups={deductibleGroups}
        addTargets={perilTargets}
        addLabel="Add deductible"
        termWording={termWording}
        emptyText={
          selectedForm
            ? "No deductibles extracted from this form."
            : "Select a policy form document to load its deductibles."
        }
        onEdit={(group, row, name, value) =>
          updateExcerpts((d) => ({
            ...d,
            deductibleInfo: applyDeductibleEdit(d.deductibleInfo, {
              perilType: row.peril,
              policyCode,
              oldName: row.name,
              newName: name,
              newValue: value,
            }),
          }))
        }
        onRemove={(group, row) =>
          updateExcerpts((d) => ({
            ...d,
            deductibleInfo: applyDeductibleDelete(d.deductibleInfo, {
              perilType: row.peril,
              policyCode,
              name: row.name,
            }),
          }))
        }
        onAdd={(peril, name, value) =>
          updateExcerpts((d) => ({
            ...d,
            deductibleInfo: applyDeductibleAdd(d.deductibleInfo, {
              perilType: peril as client.peril_type,
              policyCode,
              name,
              value,
            }),
          }))
        }
      />
      {layer === undefined && (
        <p className={styles.paneSub}>
          This policy&apos;s layer is no longer on the report, so layer
          overrides can&apos;t be edited here.
        </p>
      )}
    </div>
  );
};
